import React from 'react';
import classNames from 'classnames';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { UnreachableFunction } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { randomString, randomDate } from 'services/base/random';
import { BasePathItems, BasePath, pagePathToPathString } from 'services/base/page_path';

import { Pangolin } from 'services/accounts/types';

import { IPangolinDomain, IssueKind, Issue } from 'services/pangolin/types';

import {
  getIssueKindTitle,
  getIssueKindSubtitle,
  getIssueSeverityLabel,
  getIssueAffected,
} from 'services/pangolin/logic/issues/issues';

import { Seen, fromBoolean, getShortSeen, getMicroSeen } from 'assets/enums/seen/seen';

import {
  themedIssueKindIconImageStyles,
  getIssueKindIcon,
} from 'assets/enums/issue_kinds/issue_kinds';

import { MaliciousDomainIssueItemPartial } from './partials/issue_item_malicious_domain';
import { MaliciousAddressIssueItemPartial } from './partials/issue_item_malicious_address';
import { MalwareDomainIssueItemPartial } from './partials/issue_item_malware_domain';
import { MalwareAddressIssueItemPartial } from './partials/issue_item_malware_address';

import baseStyles from './issue_item.module.css';
import Messages from './issue_item_messages';

type IssueItemStyles = {
  issue: string;
  issueHeader: string;
  issueHeaderIcon: string;
  issueHeaderIconFiller: string;
  issueHeaderText: string;
  issueHeaderInfo: string;
  issueContent: string;

  field: string;
  fieldPart: string;
  fieldPartFiller: string;
  fieldIcon: string;
  fieldIconFiller: string;
  fieldStats: string;
  fieldStatSeverity: string;
  fieldStatCounter: string;
  fieldTextLeft: string;
  fieldTextRight: string;
  fieldInfo: string;

  details: string;
  detailsIcon: string;

  filler: string;
  seen: string;
};

const themedStyles: ThemedStyles<IssueItemStyles> = {
  [Theme.DAYLIGHT]: {
    issue: baseStyles.daylightIssue,
    issueHeader: baseStyles.issueHeader,
    issueHeaderIcon: baseStyles.issueHeaderIcon,
    issueHeaderIconFiller: baseStyles.daylightIssueHeaderIconFiller,
    issueHeaderText: baseStyles.issueHeaderText,
    issueHeaderInfo: baseStyles.issueHeaderInfo,
    issueContent: baseStyles.issueContent,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldPartFiller: baseStyles.fieldPartFiller,
    fieldIcon: baseStyles.fieldIcon,
    fieldIconFiller: baseStyles.daylightFieldIconFiller,
    fieldStats: baseStyles.fieldStats,
    fieldStatSeverity: baseStyles.fieldStatSeverity,
    fieldStatCounter: baseStyles.fieldStatCounter,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldInfo: baseStyles.fieldInfo,

    details: baseStyles.details,
    detailsIcon: baseStyles.detailsIcon,

    filler: baseStyles.daylightFiller,
    seen: baseStyles.seen,
  },
  [Theme.MIDNIGHT]: {
    issue: baseStyles.midnightIssue,
    issueHeader: baseStyles.issueHeader,
    issueHeaderIcon: baseStyles.issueHeaderIcon,
    issueHeaderIconFiller: baseStyles.midnightIssueHeaderIconFiller,
    issueHeaderText: baseStyles.issueHeaderText,
    issueHeaderInfo: baseStyles.issueHeaderInfo,
    issueContent: baseStyles.issueContent,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldPartFiller: baseStyles.fieldPartFiller,
    fieldIcon: baseStyles.fieldIcon,
    fieldIconFiller: baseStyles.midnightFieldIconFiller,
    fieldStats: baseStyles.fieldStats,
    fieldStatSeverity: baseStyles.fieldStatSeverity,
    fieldStatCounter: baseStyles.fieldStatCounter,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldInfo: baseStyles.fieldInfo,

    details: baseStyles.details,
    detailsIcon: baseStyles.detailsIcon,

    filler: baseStyles.midnightFiller,
    seen: baseStyles.seen,
  },
};

type IssueProgressProps = {
  locale: string;
  format: object;
};

export const IssueProgress = observer(({ locale, format }: IssueProgressProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.issue}>
      <div className={styles.issueHeader}>
        <div className={styles.issueHeaderIconFiller} />
        <div className={styles.issueHeaderText}>
          <div className={styles.filler}>
            {randomString(18, 22)}
          </div>
          <div className={styles.issueHeaderInfo}>
            <div className={styles.filler}>
              {randomString(20, 25)}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.issueContent}>
        <div className={styles.field}>
          <div className={styles.fieldTextLeft}>
            <div className={styles.filler}>
              {randomString(15, 20)}
            </div>
            <div className={styles.fieldInfo}>
              <div className={styles.filler}>
                {randomString(15, 20)}
              </div>
            </div>
          </div>
          <div className={styles.fieldIconFiller} />
        </div>
        <div className={styles.field}>
          <div className={styles.fieldTextLeft}>
            <div className={styles.filler}>
              {randomString(15, 20)}
            </div>
            <div className={styles.fieldInfo}>
              <div className={styles.filler}>
                {randomString(15, 20)}
              </div>
            </div>
          </div>
          <div className={styles.fieldIconFiller} />
        </div>
      </div>
      <div className={styles.fieldStats}>
        <div className={styles.details}>
          <div className={styles.filler}>
            {randomString(6, 8)}
          </div>
        </div>
        <div className={styles.details}>
          <div className={styles.filler}>{randomString(6, 8)}</div>
          &nbsp;
          <div className={styles.filler}>{randomString(6, 8)}</div>
        </div>
      </div>
      <div className={styles.fieldStats}>
        <div className={styles.details}>
          <div className={styles.filler}>
            {randomString(6, 8)}
          </div>
        </div>
        <div className={styles.details}>
          <div className={styles.filler}>
            {randomDate(new Date(2000), new Date())
             .toLocaleString(locale, format)}
          </div>
        </div>
      </div>
    </div>
  );
});

type IssueItemPartialProps = {
  domain: IPangolinDomain;
  issue: Issue;
};
const IssueItemPartial = observer(({ domain, issue }: IssueItemPartialProps) => {
  switch(issue.kind) {
    case IssueKind.UNKNOWN:
      return (
        <></>
      );
    case IssueKind.MALICIOUS_DOMAIN:
      return (
        <MaliciousDomainIssueItemPartial
          domain={domain}
          issue={issue}
        />
      );
    case IssueKind.MALICIOUS_ADDRESS:
      return (
        <MaliciousAddressIssueItemPartial
          domain={domain}
          issue={issue}
        />
      );
    case IssueKind.MALWARE_DOMAIN:
      return (
        <MalwareDomainIssueItemPartial
          domain={domain}
          issue={issue}
        />
      );
    case IssueKind.MALWARE_ADDRESS:
      return (
        <MalwareAddressIssueItemPartial
          domain={domain}
          issue={issue}
        />
      );
    default:
      return UnreachableFunction(issue);
  }
});

type IssueItemProps = {
  domain: IPangolinDomain;
  pangolin: Pangolin;
  pair: [string, Issue];
  locale: string;
  format: object;
};
export const IssueItem = observer(({ domain, pangolin, pair: [fpid, issue], locale, format }: IssueItemProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);
  const issueIconImages = useStyles(themedIssueKindIconImageStyles);
  const issueIconImage = getIssueKindIcon(issueIconImages, issue.kind);

  const onShowModal = React.useCallback(action(() => {
    domain.issuesItem = issue;
    history.push(pagePathToPathString({
      kind: BasePath.LIST_ISSUES,
      pangolinID: pangolin.id,
      itemsPath: {
        kind: BasePathItems.VIEW_ITEM,
        itemID: fpid,
      },
    }));
  }), []);

  const incident = issue.incidents.getAt(0);

  const seen = incident ? fromBoolean(issue.incidents.every(i => i.seen)) : undefined;
  const date = incident ? incident.timestamp : undefined;

  return (
    <div className={classNames(styles.issue, seen !== Seen.UNSEEN && styles.seen)} onClick={onShowModal}>
      <div className={styles.issueHeader}>
        <img className={styles.issueHeaderIcon} src={issueIconImage} alt='Issue icon' />
        <div className={styles.issueHeaderText}>
          {getIssueKindTitle(issue.kind)}
          <div className={styles.issueHeaderInfo}>
            {getIssueKindSubtitle(issue.kind)}
          </div>
        </div>
      </div>
      <div className={styles.issueContent}>
        <IssueItemPartial
          domain={domain}
          issue={issue}
        />
        <div className={styles.field}>
          <div className={styles.fieldStats}>
            <div className={styles.fieldStatSeverity}>
              {getIssueSeverityLabel(issue.severity)}
              <div className={styles.fieldInfo}>
                {Messages.severity()}
              </div>
            </div>
          </div>
          <div className={styles.fieldStats}>
            <div className={styles.fieldStatCounter}>
              {getIssueAffected(issue).length}
              <div className={styles.fieldInfo}>
                {Messages.affected()}
              </div>
            </div>
            <div className={styles.fieldStatCounter}>
              {issue.incidents.size}
              <div className={styles.fieldInfo}>
                {Messages.incidents()}
              </div>
            </div>
          </div>
        </div>
      </div>
      {
        seen != null && date != null ? (
          <div className={styles.field}>
            <div className={styles.details}>
              <span className={styles.detailsIcon}>{getMicroSeen(seen)}</span>
              {getShortSeen(seen)}
            </div>
            <div className={styles.details}>
              {date.toLocaleString(locale, format)}
            </div>
          </div>
        ) : null
      }
    </div>
  );
});
